import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Edit, Shield, Mail, Phone, Building2, Briefcase,
  Calendar, Clock, Users, BookOpen, Layers, UserCog,
  Check, X, Activity, Monitor, Globe, Key, Hash, User,
  LogIn, Smartphone,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import { BackButton, AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle, UserAvatar, StatusBadge, EmptyState, Tabs, TabsList, TabsTrigger, TabsContent } from "../ui";
import usePermissionCategories from "../../hooks/usePermissionCategories";
import "./admins.scss";

/* ── helpers ── */
function formatDate(dateStr) {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata",
  });
}

function formatDateTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata",
  }) + ", " + d.toLocaleTimeString("en-IN", {
    hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata",
  });
}

function timeAgo(dateStr) {
  if (!dateStr) return "Never";
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateStr);
}

const activityTypeIcons = {
  settings: Monitor, course: BookOpen, admin: Shield, report: Activity,
  batch: Layers, student: Users, fee: Briefcase, notification: Globe,
  interview: Calendar, enquiry: Users, instructor: UserCog, schedule: Clock,
  assignment: BookOpen, assessment: Activity, login: LogIn,
};

const activityTypeColorClass = {
  settings: "activity-icon--violet",
  course: "activity-icon--blue",
  admin: "activity-icon--violet",
  report: "activity-icon--emerald",
  batch: "activity-icon--amber",
  student: "activity-icon--cyan",
  fee: "activity-icon--emerald",
  notification: "activity-icon--blue",
  interview: "activity-icon--amber",
  enquiry: "activity-icon--cyan",
  instructor: "activity-icon--violet",
  schedule: "activity-icon--amber",
  assignment: "activity-icon--blue",
  assessment: "activity-icon--emerald",
  login: "activity-icon--blue",
};

function getRoleBadgeClass(role) {
  switch (role) {
    case "Super Admin": return "role-badge--super-admin";
    case "Moderator": return "role-badge--moderator";
    default: return "role-badge--admin";
  }
}

/* ── Profile Header ── */
function ProfileHeader({ admin }) {
  const name = `${admin.firstName} ${admin.lastName}`;

  return (
    <AppCard className="admin-profile-header">
      <AppCardContent>
        <div className="admin-profile">
          <div className="admin-profile__avatar">
            <UserAvatar name={name} size="xl" />
          </div>
          <div className="admin-profile__info">
            <div className="admin-profile__name-row">
              <h2 className="admin-profile__name">{name}</h2>
              <span className={`role-badge ${getRoleBadgeClass(admin.role)}`}>
                {admin.role || "Admin"}
              </span>
              <StatusBadge status={admin.status || "active"} />
            </div>
            <p className="admin-profile__subtitle">
              {admin.jobTitle || "—"} | {admin.department || "—"}
            </p>

            {admin.notes && (
              <p className="admin-profile__notes">{admin.notes}</p>
            )}

            <div className="admin-profile__contact-grid">
              <div className="admin-profile__contact-item">
                <Mail size={14} />
                <span>{admin.email}</span>
              </div>
              <div className="admin-profile__contact-item">
                <Phone size={14} />
                <span>{admin.mobile || "—"}</span>
              </div>
              <div className="admin-profile__contact-item">
                <Hash size={14} />
                <span>{admin.employeeId || "—"}</span>
              </div>
              <div className="admin-profile__contact-item">
                <User size={14} />
                <span>{admin.reportingManager || "None (Top Level)"}</span>
              </div>
            </div>

            <hr className="admin-profile__separator" />

            <div className="admin-profile__meta">
              <div className="admin-profile__meta-item">
                <Calendar size={12} />
                <span>Joined {formatDate(admin.joinedDate || admin.createdAt)}</span>
              </div>
              <div className="admin-profile__meta-item">
                <Clock size={12} />
                <span>Last login {admin.lastLogin ? timeAgo(admin.lastLogin) : "Never"}</span>
              </div>
            </div>
          </div>
        </div>
      </AppCardContent>
    </AppCard>
  );
}

/* ── KPI Cards ── */
function KpiCards() {
  const kpis = [
    { icon: Users, label: "Students Managed", value: 0, colorClass: "admin-kpi__icon--blue" },
    { icon: BookOpen, label: "Courses Managed", value: 0, colorClass: "admin-kpi__icon--emerald" },
    { icon: Layers, label: "Batches Managed", value: 0, colorClass: "admin-kpi__icon--amber" },
    { icon: UserCog, label: "Instructors Managed", value: 0, colorClass: "admin-kpi__icon--violet" },
  ];

  return (
    <div className="admin-kpi-grid">
      {kpis.map((kpi) => (
        <AppCard key={kpi.label} className="admin-kpi-card">
          <AppCardContent>
            <div className="admin-kpi">
              <div className={`admin-kpi__icon ${kpi.colorClass}`}>
                <kpi.icon size={20} />
              </div>
              <div>
                <p className="admin-kpi__value">{kpi.value}</p>
                <p className="admin-kpi__label">{kpi.label}</p>
              </div>
            </div>
          </AppCardContent>
        </AppCard>
      ))}
    </div>
  );
}

/* ── Permissions Tab ── */
function PermissionsTab({ admin }) {
  const { categories: permissionCategories, total: TOTAL_PERMISSIONS } = usePermissionCategories();
  const grantedPerms = (admin.permissions || []).length;

  return (
    <div>
      <div className="admin-perm-tab-header">
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <h3 className="admin-perm-tab-title">Access Permissions</h3>
          <span className="admin-perms-count">{grantedPerms} of {TOTAL_PERMISSIONS} granted</span>
        </div>
      </div>

      <div className="admin-perm-cards-grid">
        {permissionCategories.map((category) => {
          const CatIcon = category.icon;
          const catPerms = category.permissions.map((p) => p.key);
          const grantedInCat = catPerms.filter((k) => (admin.permissions || []).includes(k)).length;
          const allGranted = grantedInCat === catPerms.length;
          const noneGranted = grantedInCat === 0;

          return (
            <AppCard key={category.category} className="admin-perm-detail-card">
              <AppCardContent>
                <div className="admin-perm-detail-card__header">
                  <div className="admin-perm-detail-card__left">
                    <div className="admin-perm-detail-card__icon">
                      <CatIcon size={16} />
                    </div>
                    <span className="admin-perm-detail-card__name">{category.category}</span>
                  </div>
                  <span className={`admin-perms-count ${allGranted ? "admin-perm-badge--granted" : ""}`}>
                    {grantedInCat}/{catPerms.length}
                  </span>
                </div>

                <div className="admin-perm-badges">
                  {category.permissions.map((perm) => {
                    const granted = (admin.permissions || []).includes(perm.key);
                    return (
                      <span
                        key={perm.key}
                        className={`admin-perm-badge ${granted ? "admin-perm-badge--granted" : "admin-perm-badge--denied"}`}
                      >
                        {granted ? <Check size={12} /> : <X size={12} />}
                        {perm.label}
                      </span>
                    );
                  })}
                </div>
              </AppCardContent>
            </AppCard>
          );
        })}
      </div>
    </div>
  );
}

/* ── Quick Info Sidebar ── */
function QuickInfoCard({ admin }) {
  const items = [
    { icon: Building2, label: "Department", value: admin.department || "—" },
    { icon: Briefcase, label: "Job Title", value: admin.jobTitle || "—" },
    { icon: Hash, label: "Employee ID", value: admin.employeeId || "—" },
    { icon: User, label: "Reports To", value: admin.reportingManager || "None" },
  ];

  return (
    <AppCard className="admin-quick-info">
      <AppCardContent>
        <div className="admin-quick-title">
          <Shield size={14} />
          Quick Info
        </div>
        <div className="admin-quick-rows">
          {items.map((item) => (
            <div key={item.label} className="admin-quick-row">
              <div className="admin-quick-row__label">
                <item.icon size={12} />
                <span>{item.label}</span>
              </div>
              <span className="admin-quick-row__value">{item.value}</span>
            </div>
          ))}
        </div>
        <hr className="admin-quick-separator" />
        <div className="admin-quick-managed-grid">
          {[
            { label: "Students", value: 0, icon: Users },
            { label: "Courses", value: 0, icon: BookOpen },
            { label: "Batches", value: 0, icon: Layers },
            { label: "Instructors", value: 0, icon: UserCog },
          ].map((item) => (
            <div key={item.label} className="admin-managed-item">
              <item.icon size={12} />
              <div>
                <p className="admin-managed-item__value">{item.value}</p>
                <p className="admin-managed-item__label">{item.label}</p>
              </div>
            </div>
          ))}
        </div>
      </AppCardContent>
    </AppCard>
  );
}

/* ── Activity Tab ── */
function ActivityTab({ adminId }) {
  const [activityLog, setActivityLog] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivity = async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get(`/admins/${adminId}/activity?limit=50`);
        setActivityLog(data.activityLog || []);
        setLoginHistory(data.loginHistory || []);
      } catch {
        // Silently fail — show empty state
      }
      setLoading(false);
    };
    fetchActivity();
  }, [adminId]);

  if (loading) {
    return (
      <AppCard>
        <AppCardContent style={{ paddingTop: "1.5rem" }}>
          <p className="activity-loading">Loading activity…</p>
        </AppCardContent>
      </AppCard>
    );
  }

  if (activityLog.length === 0 && loginHistory.length === 0) {
    return (
      <AppCard>
        <AppCardContent style={{ paddingTop: "1.5rem" }}>
          <EmptyState
            icon={<Activity size={40} />}
            title="No activity recorded"
            description="No actions have been logged for this admin yet"
          />
        </AppCardContent>
      </AppCard>
    );
  }

  return (
    <div className="activity-tab">
      {/* Recent Activity */}
      {activityLog.length > 0 && (
        <AppCard>
          <AppCardHeader>
            <div className="activity-section-header">
              <AppCardTitle>
                <Activity size={16} /> Recent Activity
              </AppCardTitle>
              <span className="activity-count-badge">{activityLog.length} actions</span>
            </div>
          </AppCardHeader>
          <AppCardContent className="activity-list-content">
            <div className="activity-list">
              {activityLog.map((item) => {
                const IconComp = activityTypeIcons[item.type] || Activity;
                const colorCls = activityTypeColorClass[item.type] || "activity-icon--default";
                return (
                  <div key={item.id} className="activity-item">
                    <div className={`activity-item__icon ${colorCls}`}>
                      <IconComp size={16} />
                    </div>
                    <div className="activity-item__body">
                      <p className="activity-item__action">{item.action}</p>
                      <p className="activity-item__time">{timeAgo(item.timestamp)}</p>
                    </div>
                    <span className="activity-item__date">{formatDateTime(item.timestamp)}</span>
                  </div>
                );
              })}
            </div>
          </AppCardContent>
        </AppCard>
      )}

      {/* Login History */}
      {loginHistory.length > 0 && (
        <AppCard>
          <AppCardHeader>
            <div className="activity-section-header">
              <AppCardTitle>
                <LogIn size={16} /> Login History
              </AppCardTitle>
              <span className="activity-count-badge">{loginHistory.length} logins</span>
            </div>
          </AppCardHeader>
          <AppCardContent className="activity-list-content">
            <div className="login-history-table-wrap">
              <table className="login-history-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>IP Address</th>
                    <th>Device</th>
                  </tr>
                </thead>
                <tbody>
                  {loginHistory.map((login, idx) => (
                    <tr key={idx}>
                      <td className="login-cell--bold">{login.date}</td>
                      <td>{login.time}</td>
                      <td className="login-cell--mono">{login.ip}</td>
                      <td>
                        <span className="login-device">
                          <Smartphone size={14} />
                          {login.device}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AppCardContent>
        </AppCard>
      )}
    </div>
  );
}

/* ── Permissions Summary Sidebar ── */
function PermissionsSummaryCard({ admin }) {
  const { categories: permissionCategories, total: TOTAL_PERMISSIONS } = usePermissionCategories();
  const grantedPerms = (admin.permissions || []).length;
  const percentage = Math.round((grantedPerms / TOTAL_PERMISSIONS) * 100);

  return (
    <AppCard className="admin-perm-summary">
      <AppCardContent>
        <div className="admin-perm-summary__header">
          <div className="admin-perm-summary__left">
            <Key size={14} />
            Permissions
          </div>
          <span className="admin-perm-summary__total">{grantedPerms}/{TOTAL_PERMISSIONS}</span>
        </div>

        <div className="admin-perm-summary__bar">
          <div
            className="admin-perm-summary__bar-fill"
            style={{ width: `${percentage}%` }}
          />
        </div>

        <div className="admin-perm-summary__categories">
          {permissionCategories.map((cat) => {
            const granted = cat.permissions.filter((p) =>
              (admin.permissions || []).includes(p.key)
            ).length;
            const total = cat.permissions.length;
            const allGranted = granted === total;
            const countClass = allGranted
              ? "admin-perm-summary__cat-count--all"
              : granted > 0
              ? "admin-perm-summary__cat-count--some"
              : "admin-perm-summary__cat-count--none";

            return (
              <div key={cat.category} className="admin-perm-summary__cat-row">
                <span className="admin-perm-summary__cat-name">{cat.category}</span>
                <span className={`admin-perm-summary__cat-count ${countClass}`}>
                  {granted}/{total}
                </span>
              </div>
            );
          })}
        </div>
      </AppCardContent>
    </AppCard>
  );
}

/* ══════════════════════════════════════ */
/* ═       Admin Details Page          ═ */
/* ══════════════════════════════════════ */
const AdminDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  useEffect(() => {
    const fetchAdmin = async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get(`/admins/${id}`);
        setAdmin(data.admin);
      } catch (err) {
        setToast({
          show: true,
          message: err?.response?.data?.message || "Failed to load admin details.",
          type: "error",
        });
      }
      setLoading(false);
    };
    fetchAdmin();
  }, [id]);

  if (loading) {
    return <div className="admins-loading">Loading...</div>;
  }

  if (!admin) {
    return (
      <div className="admin-details-page">
        <EmptyState
          icon={<Shield size={40} />}
          title="Admin not found"
          description="The admin you're looking for doesn't exist or has been removed"
        />
      </div>
    );
  }

  return (
    <div className="admin-details-page">
      {/* Header */}
      <div className="admin-details-header">
        <div className="admin-details-header__left">
          <BackButton onClick={() => navigate("/admins")} />
          <div>
            <h1 className="admin-details-header__title">Admin Profile</h1>
            <p className="admin-details-header__subtitle">{admin.employeeId || admin._id}</p>
          </div>
        </div>
        <AppButton variant="outline" onClick={() => navigate(`/admins/edit/${admin._id}`)}>
          <Edit size={16} /> Edit Admin
        </AppButton>
      </div>

      {/* Two-column layout */}
      <div className="admin-details-layout">
        {/* Main column */}
        <div className="admin-details-main">
          <ProfileHeader admin={admin} />
          <KpiCards />

          <Tabs defaultValue="permissions">
            <TabsList>
              <TabsTrigger value="permissions">Permissions</TabsTrigger>
              <TabsTrigger value="activity">Activity Log</TabsTrigger>
            </TabsList>
            <TabsContent value="permissions">
              <PermissionsTab admin={admin} />
            </TabsContent>
            <TabsContent value="activity">
              <ActivityTab adminId={admin._id} />
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="admin-details-sidebar">
          <QuickInfoCard admin={admin} />
          <PermissionsSummaryCard admin={admin} />
        </div>
      </div>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default AdminDetailsPage;
